import MarketingShell from "@/components/marketing/MarketingShell";
import SEO from "@/components/marketing/SEO";

export default function Terms() {
  return (
    <MarketingShell>
      <SEO
        title="Terms of Service — TAXINPHO"
        description="Read the Terms of Service for TAXINPHO — a tax-services provider based in Balangir, Odisha. Subscription terms, acceptable use, refund window, governing law and jurisdiction."
        canonical="https://taxinpho.co/terms"
      />
      <article className="max-w-3xl mx-auto px-5 py-16 md:py-20 prose-legal">
        <p className="text-xs font-bold uppercase tracking-widest text-[#002FA7] mb-2">Legal</p>
        <h1 className="font-display font-black text-5xl tracking-tighter mb-3">Terms of Service</h1>
        <p className="text-sm text-zinc-500 mb-8">Last updated: 29 June 2026</p>

        <Section title="1. Introduction">
          Welcome to TAXINPHO (&quot;Service&quot;, &quot;Platform&quot;, &quot;we&quot;, &quot;us&quot;), a software-as-a-service
          product owned and operated by <strong>Nirmala Agrawal</strong>, sole proprietor,
          having her trade premises at <strong>Shree Krishna Kutir, Shree Ram Colony,
          Thikadar Para, Balangir, Odisha - 767001, India</strong> (&quot;Company&quot;), holding
          Trade License No. <span className="font-mono text-xs bg-zinc-100 px-1 py-0.5">TL/BLR/2023-07-14/033462</span>
          issued by Balangir Municipality. By accessing or using TAXINPHO, you
          (&quot;User&quot;, &quot;you&quot;) agree to be bound by these Terms of Service (&quot;Terms&quot;).
          If you do not agree, please discontinue use immediately.
        </Section>

        <Section title="2. Eligibility">
          You must be at least 18 years of age and legally competent to enter into a
          contract under the Indian Contract Act, 1872. By creating an account, you
          represent that you have full authority to bind any organisation on whose
          behalf you are registering.
        </Section>

        <Section title="3. Subscription &amp; Free Trial">
          <p>TAXINPHO is offered on a subscription basis. New users receive a 7-day
          free trial with full feature access, after which a paid subscription is required.
          Current pricing is published on our <a href="/pricing" className="text-[#002FA7] underline">Pricing page</a> and may change with prior notice.</p>
          <p>You authorise us to charge your selected payment method on a recurring
          basis (monthly or annual) until you cancel. Subscriptions auto-renew unless
          cancelled before the next billing date.</p>
        </Section>

        <Section title="4. Payment &amp; Taxes">
          <p>All payments are processed through our PCI-DSS compliant payment
          gateway. Prices listed are exclusive of applicable Goods and Services Tax
          (GST), which will be added at the prevailing rate (currently 18%).</p>
          <p>You are responsible for keeping your payment method valid. Failed payments
          may result in suspension of service after a 7-day grace period.</p>
        </Section>

        <Section title="5. Acceptable Use">
          <p>You agree NOT to:</p>
          <ul className="list-disc list-inside space-y-1 ml-3">
            <li>Use the Service for any unlawful, fraudulent, or harmful purpose</li>
            <li>Upload viruses, malware, or any malicious code</li>
            <li>Attempt to reverse-engineer, decompile, or copy the Platform</li>
            <li>Resell or sub-license the Service without our written permission</li>
            <li>Use the Service to send spam, phishing, or unsolicited communications</li>
            <li>Violate any privacy or data-protection laws applicable in India</li>
          </ul>
        </Section>

        <Section title="6. Data Ownership">
          <p>You retain full ownership of all data you upload to TAXINPHO, including
          client records, invoices, returns, and documents (&quot;Customer Data&quot;). We act
          merely as a data processor on your behalf.</p>
          <p>You can export or permanently delete your data at any time from the
          Settings page. Upon account closure, your data is retained for 30 days for
          recovery, then permanently deleted unless retention is required by law.</p>
        </Section>

        <Section title="7. Service Availability">
          <p>We target 99.5% monthly uptime but do not guarantee uninterrupted service.
          Planned maintenance will be communicated 48 hours in advance where possible.
          We shall not be liable for downtime caused by force majeure, government
          action, internet outages, or third-party service failures.</p>
        </Section>

        <Section title="8. Intellectual Property">
          <p>The TAXINPHO name, logo, software code, designs, and all related
          intellectual property are owned exclusively by Mrinal &amp; Associates. Your
          subscription grants you a limited, revocable, non-exclusive licence to use
          the Service — it does not transfer any IP rights.</p>
        </Section>

        <Section title="9. Disclaimer">
          <p>TAXINPHO assists with practice management but does NOT file tax returns
          on your behalf, does NOT provide legal or tax advice, and is NOT a substitute
          for professional judgement. You remain solely responsible for the accuracy of
          all filings made through official government portals.</p>
          <p>AI-generated content (HSN suggestions, message drafts, summaries) is
          informational only and must be verified before use.</p>
        </Section>

        <Section title="10. Limitation of Liability">
          To the maximum extent permitted under Indian law, our total liability for any
          claim shall not exceed the subscription fees paid by you in the 12 months
          immediately preceding the claim. We are not liable for indirect, incidental,
          consequential, or punitive damages, including lost profits or data loss.
        </Section>

        <Section title="11. Termination">
          <p>You may cancel anytime from your Billing page. Subscription continues
          until the end of the current period. We may suspend or terminate your account
          for breach of these Terms or non-payment, with reasonable notice where
          possible.</p>
        </Section>

        <Section title="12. Governing Law &amp; Jurisdiction">
          These Terms are governed by the laws of India. Any dispute arising shall be
          subject to the exclusive jurisdiction of the courts at <strong>Balangir, Odisha</strong>.
          Parties agree to attempt resolution through good-faith negotiation before
          litigation.
        </Section>

        <Section title="13. Changes to Terms">
          We may update these Terms from time to time. Material changes will be
          notified via email or in-app banner at least 7 days before they take effect.
          Continued use after the effective date constitutes acceptance.
        </Section>

        <Section title="14. Contact">
          For questions about these Terms, contact us at
          <a href="mailto:taxinpho@gmail.com" className="text-[#002FA7] underline mx-1">taxinpho@gmail.com</a>
          or write to: TAXINPHO, Shree Krishna Kutir, Shree Ram Colony, Thikadar Para,
          Balangir, Odisha - 767001, India.
        </Section>
      </article>
    </MarketingShell>
  );
}

function Section({ title, children }) {
  return (
    <section className="mb-8">
      <h2 className="font-display font-bold text-xl tracking-tight mb-3 text-zinc-900">{title}</h2>
      <div className="text-zinc-700 text-sm leading-relaxed space-y-2.5">{children}</div>
    </section>
  );
}
